import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  IconButton,
  Tooltip,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Settings as SettingsIcon,
  Sync as SyncIcon,
  Delete as DeleteIcon,
  Timeline as TimelineIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  CloudSync as CloudSyncIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

interface Platform {
  platform: string;
  enabled: boolean;
  credentials: any;
  syncSettings: {
    inventorySync: {
      enabled: boolean;
      frequency: string;
      lastSync: string | null;
      nextSync: string | null;
    };
    rateSync: {
      enabled: boolean;
      frequency: string;
      markupPercentage: number;
      lastSync: string | null;
    };
    bookingSync: {
      enabled: boolean;
      autoAccept: boolean;
      lastSync: string | null;
    };
  };
  performance: {
    totalBookings: number;
    totalRevenue: number;
    conversionRate: number;
    errors: any[];
  };
}

interface ChannelManager {
  _id: string;
  name: string;
  description: string;
  status: 'active' | 'inactive' | 'testing' | 'error';
  locationId: string;
  platforms: Platform[];
  analytics: {
    totalBookings: number;
    totalRevenue: number;
    platformBreakdown: any[];
  };
  syncHistory: any[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`channel-manager-tabpanel-${index}`}
      aria-labelledby={`channel-manager-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ChannelManagerComponent: React.FC = () => {
  const [channelManagers, setChannelManagers] = useState<ChannelManager[]>([]);
  const [selectedManager, setSelectedManager] = useState<ChannelManager | null>(null);
  const [availablePlatforms, setAvailablePlatforms] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [platformDialogOpen, setPlatformDialogOpen] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);
  const [newManager, setNewManager] = useState({
    name: '',
    description: '',
    locationId: ''
  });
  const [newPlatform, setNewPlatform] = useState({
    platform: '',
    enabled: true,
    credentials: {},
    syncSettings: {
      inventorySync: { enabled: true, frequency: 'real-time' },
      rateSync: { enabled: true, frequency: 'hourly', markupPercentage: 0 },
      bookingSync: { enabled: true, autoAccept: true }
    }
  });

  useEffect(() => {
    fetchChannelManagers();
    fetchAvailablePlatforms();
  }, []);

  const fetchChannelManagers = async () => {
    try {
      const response = await fetch('/api/channel-manager/location/675e7c5e123456789abcdef0', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setChannelManagers(data);
        if (data.length > 0) {
          setSelectedManager(data[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching channel managers:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailablePlatforms = async () => {
    try {
      const response = await fetch('/api/channel-manager/meta/platforms', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailablePlatforms(data.platforms);
      }
    } catch (error) {
      console.error('Error fetching available platforms:', error);
    }
  };

  const handleCreateManager = async () => {
    try {
      const response = await fetch('/api/channel-manager', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          ...newManager,
          locationId: '675e7c5e123456789abcdef0' // Default location ID
        })
      });

      if (response.ok) {
        setDialogOpen(false);
        setNewManager({ name: '', description: '', locationId: '' });
        fetchChannelManagers();
      }
    } catch (error) {
      console.error('Error creating channel manager:', error);
    }
  };

  const handleAddPlatform = async () => {
    if (!selectedManager) return;

    try {
      const response = await fetch(`/api/channel-manager/${selectedManager._id}/platforms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newPlatform)
      });

      if (response.ok) {
        setPlatformDialogOpen(false);
        setNewPlatform({
          platform: '',
          enabled: true,
          credentials: {},
          syncSettings: {
            inventorySync: { enabled: true, frequency: 'real-time' },
            rateSync: { enabled: true, frequency: 'hourly', markupPercentage: 0 },
            bookingSync: { enabled: true, autoAccept: true }
          }
        });
        fetchChannelManagers();
      }
    } catch (error) {
      console.error('Error adding platform:', error);
    }
  };

  const handleSyncInventory = async () => {
    if (!selectedManager) return;

    setSyncInProgress(true);
    try {
      const response = await fetch(`/api/channel-manager/${selectedManager._id}/sync/inventory`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Sync results:', data);
        fetchChannelManagers(); // Refresh data
      }
    } catch (error) {
      console.error('Error syncing inventory:', error);
    } finally {
      setSyncInProgress(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'default';
      case 'testing': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  const formatLastSync = (lastSync: string | null) => {
    if (!lastSync) return 'Never';
    return new Date(lastSync).toLocaleString();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading channel managers...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Channel Manager</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          Create Channel Manager
        </Button>
      </Box>

      {channelManagers.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CloudSyncIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Channel Managers Configured
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Set up a channel manager to sync inventory and bookings across multiple platforms
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialogOpen(true)}
            >
              Create Your First Channel Manager
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {/* Channel Manager List */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Channel Managers
                </Typography>
                <List>
                  {channelManagers.map((manager) => (
                    <ListItem
                      key={manager._id}
                      button
                      selected={selectedManager?._id === manager._id}
                      onClick={() => setSelectedManager(manager)}
                    >
                      <ListItemIcon>
                        <CloudSyncIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary={manager.name}
                        secondary={
                          <Box>
                            <Chip
                              label={manager.status}
                              color={getStatusColor(manager.status) as any}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Typography variant="caption" display="block">
                              {manager.platforms.length} platforms
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>

          {/* Channel Manager Details */}
          <Grid item xs={12} md={8}>
            {selectedManager && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">{selectedManager.name}</Typography>
                    <Box>
                      <Button
                        startIcon={<SyncIcon />}
                        onClick={handleSyncInventory}
                        disabled={syncInProgress}
                        sx={{ mr: 1 }}
                      >
                        {syncInProgress ? 'Syncing...' : 'Sync All'}
                      </Button>
                      <Button
                        startIcon={<AddIcon />}
                        variant="contained"
                        onClick={() => setPlatformDialogOpen(true)}
                      >
                        Add Platform
                      </Button>
                    </Box>
                  </Box>

                  <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                    <Tab label="Platforms" />
                    <Tab label="Analytics" />
                    <Tab label="Sync History" />
                  </Tabs>

                  <TabPanel value={tabValue} index={0}>
                    {selectedManager.platforms.length === 0 ? (
                      <Alert severity="info">
                        <AlertTitle>No Platforms Configured</AlertTitle>
                        Add platforms to start syncing inventory and managing bookings across multiple channels.
                      </Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {selectedManager.platforms.map((platform, index) => (
                          <Grid item xs={12} key={index}>
                            <Accordion>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                  <Typography sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                                    {platform.platform.replace('_', '.').toUpperCase()}
                                  </Typography>
                                  <Chip
                                    label={platform.enabled ? 'Enabled' : 'Disabled'}
                                    color={platform.enabled ? 'success' : 'default'}
                                    size="small"
                                    sx={{ mr: 2 }}
                                  />
                                  <Chip
                                    label={`${platform.performance.totalBookings} bookings`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <Grid container spacing={2}>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Inventory Sync
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Frequency: {platform.syncSettings.inventorySync.frequency}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Last Sync: {formatLastSync(platform.syncSettings.inventorySync.lastSync)}
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Rate Sync
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Frequency: {platform.syncSettings.rateSync.frequency}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Markup: {platform.syncSettings.rateSync.markupPercentage}%
                                    </Typography>
                                  </Grid>
                                  <Grid item xs={12} md={4}>
                                    <Typography variant="subtitle2" gutterBottom>
                                      Performance
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Revenue: ${platform.performance.totalRevenue.toLocaleString()}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Conversion: {platform.performance.conversionRate.toFixed(1)}%
                                    </Typography>
                                  </Grid>
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </TabPanel>

                  <TabPanel value={tabValue} index={1}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUpIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedManager.analytics.totalBookings}
                            </Typography>
                            <Typography color="text.secondary">
                              Total Bookings
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <AssessmentIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                            <Typography variant="h4">
                              ${selectedManager.analytics.totalRevenue.toLocaleString()}
                            </Typography>
                            <Typography color="text.secondary">
                              Total Revenue
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ textAlign: 'center' }}>
                            <CloudSyncIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                            <Typography variant="h4">
                              {selectedManager.platforms.filter(p => p.enabled).length}
                            </Typography>
                            <Typography color="text.secondary">
                              Active Platforms
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>

                    {selectedManager.analytics.platformBreakdown.length > 0 && (
                      <Box sx={{ mt: 3 }}>
                        <Typography variant="h6" gutterBottom>
                          Platform Performance
                        </Typography>
                        <TableContainer component={Paper}>
                          <Table>
                            <TableHead>
                              <TableRow>
                                <TableCell>Platform</TableCell>
                                <TableCell align="right">Bookings</TableCell>
                                <TableCell align="right">Revenue</TableCell>
                                <TableCell align="right">Conversion Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {selectedManager.analytics.platformBreakdown.map((platform) => (
                                <TableRow key={platform.platform}>
                                  <TableCell>{platform.platform.toUpperCase()}</TableCell>
                                  <TableCell align="right">{platform.bookings}</TableCell>
                                  <TableCell align="right">${platform.revenue.toLocaleString()}</TableCell>
                                  <TableCell align="right">{platform.conversionRate.toFixed(1)}%</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </TabPanel>

                  <TabPanel value={tabValue} index={2}>
                    <Typography variant="h6" gutterBottom>
                      Recent Sync Activities
                    </Typography>
                    {selectedManager.syncHistory.length === 0 ? (
                      <Alert severity="info">
                        No sync history available yet.
                      </Alert>
                    ) : (
                      <List>
                        {selectedManager.syncHistory.slice(0, 10).map((sync, index) => (
                          <React.Fragment key={index}>
                            <ListItem>
                              <ListItemIcon>
                                {sync.status === 'success' ? (
                                  <CheckCircleIcon color="success" />
                                ) : sync.status === 'error' ? (
                                  <ErrorIcon color="error" />
                                ) : (
                                  <WarningIcon color="warning" />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={`${sync.operation} - ${sync.platform}`}
                                secondary={`${new Date(sync.timestamp).toLocaleString()} - ${sync.status}`}
                              />
                            </ListItem>
                            {index < selectedManager.syncHistory.length - 1 && <Divider />}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </TabPanel>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}

      {/* Create Channel Manager Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Channel Manager</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newManager.name}
            onChange={(e) => setNewManager({ ...newManager, name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newManager.description}
            onChange={(e) => setNewManager({ ...newManager, description: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateManager} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Add Platform Dialog */}
      <Dialog open={platformDialogOpen} onClose={() => setPlatformDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Platform</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Platform</InputLabel>
            <Select
              value={newPlatform.platform}
              onChange={(e) => setNewPlatform({ ...newPlatform, platform: e.target.value })}
            >
              {availablePlatforms.map((platform) => (
                <MenuItem key={platform.id} value={platform.id}>
                  {platform.name} - {platform.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={newPlatform.enabled}
                onChange={(e) => setNewPlatform({ ...newPlatform, enabled: e.target.checked })}
              />
            }
            label="Enable Platform"
            sx={{ mb: 2 }}
          />

          <Typography variant="h6" gutterBottom>
            Sync Settings
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Inventory Sync Frequency</InputLabel>
                <Select
                  value={newPlatform.syncSettings.inventorySync.frequency}
                  onChange={(e) => setNewPlatform({
                    ...newPlatform,
                    syncSettings: {
                      ...newPlatform.syncSettings,
                      inventorySync: {
                        ...newPlatform.syncSettings.inventorySync,
                        frequency: e.target.value
                      }
                    }
                  })}
                >
                  <MenuItem value="real-time">Real-time</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Rate Sync Frequency</InputLabel>
                <Select
                  value={newPlatform.syncSettings.rateSync.frequency}
                  onChange={(e) => setNewPlatform({
                    ...newPlatform,
                    syncSettings: {
                      ...newPlatform.syncSettings,
                      rateSync: {
                        ...newPlatform.syncSettings.rateSync,
                        frequency: e.target.value
                      }
                    }
                  })}
                >
                  <MenuItem value="real-time">Real-time</MenuItem>
                  <MenuItem value="hourly">Hourly</MenuItem>
                  <MenuItem value="daily">Daily</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Rate Markup (%)"
                type="number"
                fullWidth
                value={newPlatform.syncSettings.rateSync.markupPercentage}
                onChange={(e) => setNewPlatform({
                  ...newPlatform,
                  syncSettings: {
                    ...newPlatform.syncSettings,
                    rateSync: {
                      ...newPlatform.syncSettings.rateSync,
                      markupPercentage: parseFloat(e.target.value) || 0
                    }
                  }
                })}
              />
            </Grid>
          </Grid>

          <FormControlLabel
            control={
              <Switch
                checked={newPlatform.syncSettings.bookingSync.autoAccept}
                onChange={(e) => setNewPlatform({
                  ...newPlatform,
                  syncSettings: {
                    ...newPlatform.syncSettings,
                    bookingSync: {
                      ...newPlatform.syncSettings.bookingSync,
                      autoAccept: e.target.checked
                    }
                  }
                })}
              />
            }
            label="Auto-Accept Bookings"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlatformDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddPlatform} variant="contained">Add Platform</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChannelManagerComponent;